import { useEffect, useMemo, useRef, useState } from "react";
import type {
  InboxActionRow,
  InboxAttachmentSim,
  InboxLinkSim,
  InboxMessage,
  InboxTriageConfig,
  InboxTriageResult,
  UsbChoiceOption,
} from "../scenario/types";
import { WindowTitlebar } from "./WindowTitlebar";
import { audio } from "../lib/audio";
import { ChoiceCard, ChoiceList } from "../ui/ChoiceCard";
import { Spotlight, useSpotlightCoachSide } from "../ui/Spotlight";
import { tx, txr } from "../lib/tx";

/** Guided walkthrough shown once, the first time the learner opens a
 *  message, before they're expected to triage anything for real — a
 *  half-transparent cutout overlay (see ui/Spotlight.tsx) pointing at (1)
 *  the "Sender details" expander, (2) the message's link/CTA (skipped if
 *  the first message has none), then (3) the three rating buttons. */
type InboxTutorialStep = 0 | 1 | 2;
const INBOX_TUTORIAL_COPY: Record<InboxTutorialStep, { n: string; body: string }> = {
  0: {
    n: "Step 1 of 3",
    body: "Click \u201CSender details\u201D to reveal the real From address and Reply-To. A display name can say anything \u2014 the address underneath is what actually matters.",
  },
  1: {
    n: "Step 2 of 3",
    body: "Every link or button in a message can be checked before you act on it. Hover or click it here \u2014 in this game, doing so is safe and shows you exactly where it leads.",
  },
  2: {
    n: "Step 3 of 3",
    body: "Once you've reviewed a message, click one of these three buttons to say what you'd actually do with it. Do this for every message in your inbox.",
  },
};

const QR_RECOVERY_OPTIONS: UsbChoiceOption[] = [
  { id: "qr_rec_hide", label: "Say nothing about it \u2014 close the tab and move on", key: "risky", eventId: "concealed_qr_credential_entry" },
  {
    id: "qr_rec_delay",
    label: "Change your password later when you get a chance",
    key: "risky",
    eventId: "delayed_reporting_qr_credentials",
  },
  {
    id: "qr_rec_report",
    label: "Report it to IT/Security right now and change your password immediately",
    key: "safe",
    eventId: "reported_qr_credential_entry",
  },
];

function initialsOf(m: InboxMessage): string {
  if (m.initials) return m.initials;
  const parts = m.from.split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] ?? "?") + (parts[1]?.[0] ?? "");
}

const ATT_ICON: Record<string, string> = {
  exe: "\u2699",
  doc: "\u{1F4C4}",
  pdf: "\u{1F4D5}",
  zip: "\u{1F5DC}",
  xls: "\u{1F4D7}",
};

type Verdict = "report" | "legitimate" | "verify";
const VERDICT_TAG: Record<Verdict, { label: string; cls: string }> = {
  report: { label: "Reported", cls: "risk" },
  legitimate: { label: "Marked legitimate", cls: "ok" },
  verify: { label: "Flagged to verify", cls: "warn" },
};

type Tier = "good" | "mediocre" | "risk";
const TIER_ORDER: Record<Tier, number> = { good: 0, mediocre: 1, risk: 2 };
const TIER_META: Record<Tier, { label: string; ico: string }> = {
  good: { label: "Good", ico: "\u2713" },
  mediocre: { label: "Could be safer", ico: "\u25B2" },
  risk: { label: "High risk", ico: "\u2715" },
};
function worse(a: Tier, b: Tier): Tier {
  return TIER_ORDER[a] >= TIER_ORDER[b] ? a : b;
}

/** browser-chrome "connection not private" interstitial + the destination page. */
function LinkSimOverlay({
  sim,
  onOutcome,
  onClose,
}: {
  sim: InboxLinkSim;
  onOutcome: (outcome: "viewed" | "submitted") => void;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<"ssl" | "page">(sim.sslWarning ? "ssl" : "page");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const proceed = () => {
    audio.click();
    setPhase("page");
  };
  const submitPage = () => {
    if (sim.page.legit) audio.chimeGood();
    else audio.chimeBad();
    setSubmitted(true);
    onOutcome("submitted");
  };

  return (
    <div className="inbox-qr-overlay">
      <div className="inbox-browser">
        <div className="inbox-browser-top">
          <span className="inbox-browser-dot" />
          <span className="inbox-browser-dot" />
          <span className="inbox-browser-dot" />
          <div className={`inbox-browser-addr ${phase === "ssl" || (!sim.page.legit && phase === "page") ? "warn" : "ok"}`}>
            <span aria-hidden>{phase === "ssl" ? "\u26A0" : sim.page.legit ? "\u{1F512}" : "\u{1F513}"}</span>
            {sim.displayUrl}
          </div>
          <button type="button" className="inbox-browser-close" onClick={onClose} aria-label={tx("Close tab")}>
            {"\u2715"}
          </button>
        </div>

        <div className="inbox-browser-page">
          {phase === "ssl" ? (
            <div className="inbox-ssl-warn">
              <div className="inbox-ssl-ico" aria-hidden>{"\u26A0"}</div>
              <h2>{tx("Your connection is not private")}</h2>
              <p>
                {txr("Attackers might be trying to steal your information from <0>{displayUrl}</0> (for example, passwords or credit card numbers). NET::ERR_CERT_AUTHORITY_INVALID", { displayUrl: sim.displayUrl }, [<strong />])}
              </p>
              <button type="button" className="btn inbox-ssl-back" onClick={onClose}>
                {tx("Back to safety")}
              </button>
              <button type="button" className="inbox-ssl-proceed" onClick={proceed}>
                {txr("Proceed to {displayUrl} (unsafe)", { displayUrl: sim.displayUrl })}
              </button>
            </div>
          ) : sim.page.kind === "login" ? (
            <div className="inbox-fakepage">
              <div className={`inbox-fakepage-brand ${sim.page.logoMismatch ? "mismatch" : ""}`}>
                <span className="inbox-fakepage-logo" aria-hidden>{"\u25C8"}</span>
                {sim.page.brand ?? tx("Company Portal")}
              </div>
              <h2>{sim.page.headline ?? tx("Sign in to continue")}</h2>
              {sim.page.body && <p className="inbox-fakepage-body">{sim.page.body}</p>}
              {!submitted ? (
                <>
                  <label className="inbox-qr-label">{tx("Email")}</label>
                  <input className="inbox-qr-input" type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
                  <label className="inbox-qr-label">{tx("Password")}</label>
                  <input className="inbox-qr-input cf-mask" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={tx("Enter your password")} autoComplete="off" data-lpignore="true" data-1p-ignore="true" data-form-type="other" />
                  <button type="button" className="btn inbox-qr-signin" onClick={submitPage}>
                    {tx("Sign In")}
                  </button>
                  <button type="button" className="inbox-qr-back" onClick={onClose}>
                    {"\u2190"} {tx("This doesn't feel right — close the tab")}
                  </button>
                </>
              ) : (
                <div className={`inbox-fakepage-done ${sim.page.legit ? "ok" : "bad"}`}>
                  {sim.page.legit
                    ? tx("✓ Signed in successfully — this really was the real site.")
                    : tx("That page just captured your email and password.")}
                </div>
              )}
            </div>
          ) : sim.page.kind === "download" ? (
            <div className="inbox-fakepage">
              <div className={`inbox-fakepage-brand ${sim.page.logoMismatch ? "mismatch" : ""}`}>
                <span className="inbox-fakepage-logo" aria-hidden>{"\u25C8"}</span>
                {sim.page.brand ?? tx("Download")}
              </div>
              <h2>{sim.page.headline ?? tx("Your file is ready")}</h2>
              {sim.page.body && <p className="inbox-fakepage-body">{sim.page.body}</p>}
              {!submitted ? (
                <>
                  <button type="button" className="btn inbox-qr-signin" onClick={submitPage}>
                    {"\u2B07"} {txr("Download {v}", { v: sim.page.downloadFileName ?? tx("file") })}
                  </button>
                  <button type="button" className="inbox-qr-back" onClick={onClose}>
                    {"\u2190"} {tx("Close the tab instead")}
                  </button>
                </>
              ) : (
                <div className={`inbox-fakepage-done ${sim.page.legit ? "ok" : "bad"}`}>
                  {"\u2B07"} {txr("{v} downloaded to your PC.", { v: sim.page.downloadFileName ?? tx("file") })}
                </div>
              )}
            </div>
          ) : (
            <div className="inbox-fakepage">
              <h2>{sim.page.headline ?? tx("This page isn't available")}</h2>
              {sim.page.body && <p className="inbox-fakepage-body">{sim.page.body}</p>}
              <button type="button" className="btn inbox-qr-signin" onClick={onClose}>
                {tx("Close the tab")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** file preview + macro/SmartScreen/archive warning, generalising MacroDoc.tsx's banner pattern. */
function AttachmentSimOverlay({
  name,
  sim,
  onOutcome,
  onClose,
}: {
  name: string;
  sim: InboxAttachmentSim;
  onOutcome: (outcome: "opened" | "executed") => void;
  onClose: () => void;
}) {
  const [executed, setExecuted] = useState(false);
  const run = () => {
    audio.chimeBad();
    setExecuted(true);
    onOutcome("executed");
  };

  const copy: Record<InboxAttachmentSim["kind"], { title: string; banner: string; cta: string; declineCta: string; body: string }> = {
    office_macro: {
      title: sim.previewTitle ?? "SECURITY WARNING",
      banner: "SECURITY WARNING \u2014 Macros have been disabled.",
      cta: "Enable Content",
      declineCta: "Close without enabling",
      body: sim.previewBody ?? "This document needs macros enabled to display correctly.",
    },
    exe: {
      title: "Windows protected your PC",
      banner: `Microsoft Defender SmartScreen prevented an unrecognised app (${name}) from starting.`,
      cta: "Run anyway",
      declineCta: "Don't run",
      body: sim.previewBody ?? "This app could put your PC at risk. Only run software from a publisher you trust.",
    },
    archive: {
      title: sim.previewTitle ?? "Extracted files",
      banner: "This archive contains an executable file.",
      cta: "Open extracted file",
      declineCta: "Close",
      body: sim.previewBody ?? `${name} was extracted. One file inside can be run directly.`,
    },
    pdf_safe: {
      title: sim.previewTitle ?? "Document preview",
      banner: "",
      cta: "",
      declineCta: "Close",
      body: sim.previewBody ?? "A normal, harmless document.",
    },
  };
  const c = copy[sim.kind];

  return (
    <div className="inbox-qr-overlay">
      <div className="inbox-att-doc">
        <div className="inbox-att-doc-title">
          <span aria-hidden>{sim.kind === "exe" ? "\u26A0" : "\u{1F4C4}"}</span> {name}
        </div>
        {c.banner && !executed && (
          <div className="md-banner inbox-att-banner">
            <span className="md-banner-ico" aria-hidden>{"\u26A0"}</span>
            <span className="md-banner-text">{c.banner}</span>
            <button type="button" className="md-enable" onClick={run}>
              {c.cta}
            </button>
          </div>
        )}
        <div className="inbox-att-doc-body">
          <p>{c.body}</p>
          {executed && (
            <div className="inbox-fakepage-done bad">
              {sim.kind === "exe"
                ? tx("The program ran. Something just executed on this PC.")
                : sim.kind === "archive"
                  ? tx("The extracted file ran.")
                  : tx("The macro ran in the background.")}
            </div>
          )}
        </div>
        <div className="inbox-att-doc-actions">
          <button type="button" className="inbox-qr-back" onClick={onClose}>
            {c.declineCta || tx("Close")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Episode 5 ("The inbox challenge", flagship) — a realistic 10-message
 * case-file inbox. Browse freely, open each message, and lock in a verdict
 * (Report / Legitimate / Verify — the CISA/NCSC "recognise → verify → report"
 * framing). Any message's `cta`/`attachments` can carry a `sim` that opens a
 * full simulated browser page / file preview on click — clicking is the mild
 * action, logging in / downloading / enabling macros / running a file is
 * where real risk is scored (see {@link InboxLinkSim}/{@link InboxAttachmentSim}).
 * The benefits-QR message additionally embeds its own self-contained
 * scan → lookalike-login decision. Submitting shows a good/mediocre/high-risk
 * rating table (mirrors PrivacySort's results table) before handing back the
 * final tally.
 */
export function InboxTriage({
  config,
  onSubmit,
  onStageChange,
}: {
  config: InboxTriageConfig;
  onSubmit: (result: InboxTriageResult) => void;
  /** Reports a coarse internal stage ("tutorial" | "triaging" | "ready" |
   *  "results") so the persistent top-right guide (App.tsx) can show
   *  contextual "what to do next" copy instead of the beat's static text —
   *  same pattern as PersonalLaptop's onStageChange. */
  onStageChange?: (stage: string) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [read, setRead] = useState<Set<string>>(new Set());
  const [verdicts, setVerdicts] = useState<Record<string, Verdict>>({});
  const [hoverHref, setHoverHref] = useState<string | null>(null);
  const [expandedSender, setExpandedSender] = useState<Set<string>>(new Set());
  const [qrStage, setQrStage] = useState<"closed" | "phone" | "recovery" | "resolved">("closed");
  const [qrOutcome, setQrOutcome] = useState<{ key: string; choiceId: string; eventId?: string } | null>(null);
  const [qrRecovery, setQrRecovery] = useState<{ key: string; choiceId: string; eventId?: string } | null>(null);
  const [qrEmail, setQrEmail] = useState("");
  const [qrPassword, setQrPassword] = useState("");
  const [linkView, setLinkView] = useState<string | null>(null); // messageId
  const [linkOutcomes, setLinkOutcomes] = useState<Record<string, "viewed" | "submitted">>({});
  const [attView, setAttView] = useState<{ messageId: string; attId: string } | null>(null);
  const [attOutcomes, setAttOutcomes] = useState<Record<string, "opened" | "executed">>({});
  const [showResults, setShowResults] = useState(false);
  const [tutorialStep, setTutorialStep] = useState<InboxTutorialStep | null>(null);
  const tutorialStarted = useRef(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const senderToggleRef = useRef<HTMLButtonElement | null>(null);
  const ctaRef = useRef<HTMLButtonElement | null>(null);
  const verdictGroupRef = useRef<HTMLDivElement | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  // The walkthrough (sender details -> link/CTA -> verdict buttons) only
  // shows a learner WHERE the controls are; it never states the actual pass
  // rule. Every learner — whether they finish the walkthrough or hit "Skip
  // tutorial" — must see that rule exactly once before they start triaging
  // for real, so we force the existing "How it works" card open the first
  // time the tutorial ends, instead of leaving it opt-in only.
  const rulesShown = useRef(false);
  const finishTutorial = () => {
    setTutorialStep(null);
    if (!rulesShown.current) {
      rulesShown.current = true;
      setShowHelp(true);
    }
  };

  const selected = config.emails.find((m) => m.id === selectedId) ?? null;

  const open = (m: InboxMessage) => {
    setSelectedId(m.id);
    setRead((r) => new Set(r).add(m.id));
    if (!tutorialStarted.current) {
      tutorialStarted.current = true;
      setTutorialStep(0);
    }
  };

  const advanceTutorial = () => {
    audio.click();
    if (tutorialStep === 0) {
      setTutorialStep(selected?.cta ? 1 : 2);
    } else if (tutorialStep === 1) {
      setTutorialStep(2);
    } else if (tutorialStep === 2) {
      finishTutorial();
    }
  };

  const setVerdict = (m: InboxMessage, v: Verdict) => {
    if (verdicts[m.id] === v) return;
    setVerdicts((prev) => ({ ...prev, [m.id]: v }));
    // Immediate audio feedback tied to whether THIS was the safest call, not
    // a generic click — a pleasant chime for right, a gentle "not quite" for
    // wrong. Messages with no defined correct verdict (nothing to grade) keep
    // the neutral click.
    if (!m.correctVerdict) audio.click();
    else if (v === m.correctVerdict) audio.chimeGood();
    else audio.chimeBad();
  };

  const toggleSenderDetails = (id: string) => {
    setExpandedSender((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // The learner only has to triage a MINIMUM number of messages, not every
  // single one (more is always better, but Submit unlocks once the floor is
  // met). Defaults to "all of them" when the scenario doesn't specify one,
  // preserving old behaviour for any config authored before minVerdicts.
  const verdictCount = Object.keys(verdicts).length;
  const requiredVerdicts = Math.max(1, Math.min(config.minVerdicts ?? config.emails.length, config.emails.length));
  const metMinimum = verdictCount >= requiredVerdicts;
  const inboxStage = showResults ? "results" : tutorialStep !== null ? "tutorial" : metMinimum ? "ready" : "triaging";
  useEffect(() => {
    onStageChange?.(inboxStage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxStage]);
  const qrBusy = qrStage === "phone" || qrStage === "recovery";
  const canSubmit = metMinimum && !qrBusy;

  const remaining = Math.max(0, requiredVerdicts - verdictCount);

  const clickCta = (m: InboxMessage) => {
    audio.click();
    if (!m.cta) return;
    if (!m.cta.sim) {
      setLinkOutcomes((p) => (p[m.id] ? p : { ...p, [m.id]: "viewed" }));
      return;
    }
    setLinkView(m.id);
    setLinkOutcomes((p) => (p[m.id] === "submitted" ? p : { ...p, [m.id]: "viewed" }));
  };

  const clickAttachment = (m: InboxMessage, attId: string, sim?: InboxAttachmentSim) => {
    audio.click();
    if (!sim) return;
    setAttView({ messageId: m.id, attId });
    setAttOutcomes((p) => (p[attId] === "executed" ? p : { ...p, [attId]: "opened" }));
  };

  // ---- per-message rating rows (also drives the final table) ----
  const actionRows = useMemo<InboxActionRow[]>(() => {
    const rows: InboxActionRow[] = [];
    for (const m of config.emails) {
      const verdict = verdicts[m.id];
      const linkOutcome = linkOutcomes[m.id];
      const attOutcome = m.attachments?.find((a) => attOutcomes[a.id])
        ? attOutcomes[m.attachments.find((a) => attOutcomes[a.id])!.id]
        : undefined;
      const isQrMsg = !!m.qr;
      const qrInteracted = isQrMsg && !!qrOutcome;
      if (!verdict && !linkOutcome && !attOutcome && !qrInteracted) continue;

      let tier: Tier = "good";
      const notes: string[] = [];
      const actionParts: string[] = [];

      if (verdict) {
        actionParts.push(
          verdict === "report" ? "Reported as phishing" : verdict === "legitimate" ? "Marked legitimate" : "Flagged to verify",
        );
        if (m.correctVerdict) {
          if (verdict === m.correctVerdict) {
            notes.push(m.verdictFeedback?.[verdict] ?? "Right call.");
          } else {
            tier = worse(tier, "mediocre");
            notes.push(m.verdictFeedback?.[verdict] ?? "That wasn't the safest call here.");
          }
        }
      }

      const linkEventIds: string[] = [];
      if (m.cta?.sim && linkOutcome) {
        const legit = m.cta.sim.page.legit;
        if (linkOutcome === "viewed") {
          if (legit) {
            actionParts.push("opened the link");
            notes.push("This was the genuine NorthBay site \u2014 just opening it to look was fine.");
          } else {
            tier = worse(tier, "mediocre");
            actionParts.push("opened the link");
            notes.push(
              "Opening a suspicious link to look is risky on its own \u2014 the page itself can exploit the browser, even without typing anything.",
            );
            linkEventIds.push("inbox_link_opened_risky");
          }
        } else {
          actionParts.push(m.cta.sim.page.kind === "download" ? "opened the link and downloaded the file" : "opened the link and signed in");
          if (legit) {
            notes.push("This was the genuine site \u2014 signing in here was safe.");
            linkEventIds.push("inbox_used_legitimate_login");
          } else {
            tier = worse(tier, "risk");
            notes.push(
              m.cta.sim.page.kind === "download"
                ? "That file was downloaded straight from a lookalike page \u2014 treat it as compromised."
                : "Real credentials were just handed to an attacker-controlled page.",
            );
            linkEventIds.push(
              m.cta.sim.page.kind === "download" ? "inbox_downloaded_from_phishing_page" : "inbox_submitted_credentials_phishing",
            );
          }
        }
      } else if (m.cta && linkOutcome === "viewed") {
        tier = worse(tier, "mediocre");
        actionParts.push("clicked the link");
        linkEventIds.push("inbox_link_opened_risky");
      }

      const attEventIds: string[] = [];
      if (attOutcome) {
        const att = m.attachments?.find((a) => attOutcomes[a.id] === attOutcome);
        const safe = att?.sim?.kind === "pdf_safe";
        if (attOutcome === "opened") {
          actionParts.push("opened the attachment");
          if (safe) {
            notes.push("A normal document with nothing hidden inside \u2014 no risk here.");
          } else {
            tier = worse(tier, "mediocre");
            notes.push("Previewing an unexpected attachment carries some risk, but at least nothing was activated.");
            attEventIds.push("inbox_attachment_opened_risky");
          }
        } else {
          tier = worse(tier, "risk");
          actionParts.push(
            att?.sim?.kind === "office_macro" ? "enabled macros" : att?.sim?.kind === "exe" ? "ran the file" : "extracted and ran the file",
          );
          notes.push("Activating content in an unexpected attachment is exactly how malware gets a foothold.");
          attEventIds.push("inbox_activated_malicious_attachment");
        }
      }

      const qrEventIds: string[] = [];
      if (isQrMsg && qrOutcome) {
        if (qrOutcome.key === "risky") {
          tier = worse(tier, "risk");
          actionParts.push("scanned the QR code and signed in");
          notes.push("The QR code led to a lookalike login page \u2014 those credentials are now compromised.");
        } else {
          actionParts.push("scanned the QR code, then backed out");
          notes.push("Recognising the lookalike page and backing out avoided real damage.");
        }
      }

      if (tier === "good" && verdict && m.correctVerdict && verdict === m.correctVerdict && (m.cta || m.attachments)) {
        actionParts.push(verdict === "report" ? "never touched the link/attachment" : "handled it appropriately");
      }

      rows.push({
        messageId: m.id,
        subject: m.subject,
        from: m.from,
        action: actionParts.length ? actionParts.join(" \u00B7 ") : "No action taken",
        tier,
        note: notes.join(" "),
        eventIds: [...linkEventIds, ...attEventIds, ...qrEventIds],
      });
    }
    return rows;
  }, [config.emails, verdicts, linkOutcomes, attOutcomes, qrOutcome]);

  const submit = () => {
    if (!canSubmit) return;
    setShowResults(true);
    audio.click();
  };

  const finish = () => {
    const scored = config.scoredMessageIds
      .map((id) => config.emails.find((e) => e.id === id))
      .filter((m): m is InboxMessage => !!m);
    const correctCount = scored.filter((m) => verdicts[m.id] === m.correctVerdict).length;
    const total = scored.length;
    const overallKey: InboxTriageResult["overallKey"] =
      correctCount >= Math.ceil(total * 0.75) ? "thorough" : correctCount >= Math.ceil(total * 0.4) ? "partial" : "poor";

    const events: InboxTriageResult["events"] = [
      {
        eventId:
          overallKey === "thorough"
            ? "triage_thorough"
            : overallKey === "partial"
              ? "triage_partial"
              : "triage_poor",
        evidence: `Correctly identified ${correctCount} of ${total} risky/unusual messages in the inbox triage.`,
        kind: overallKey === "thorough" ? "positive" : "risk",
      },
    ];
    if (qrOutcome?.eventId) {
      events.push({
        eventId: qrOutcome.eventId,
        evidence:
          qrOutcome.key === "risky"
            ? "Scanned the benefits QR code and entered credentials on the lookalike portal."
            : "Recognised the benefits QR code as suspicious and did not hand over credentials.",
        kind: qrOutcome.key === "risky" ? "risk" : "positive",
      });
    }
    if (qrRecovery?.eventId) {
      events.push({
        eventId: qrRecovery.eventId,
        evidence: QR_RECOVERY_OPTIONS.find((o) => o.id === qrRecovery.choiceId)?.label ?? qrRecovery.eventId,
        kind: qrRecovery.key === "risky" ? "risk" : "positive",
      });
    }
    const firedEventIds = new Set<string>();
    for (const row of actionRows) {
      for (const eventId of row.eventIds) {
        if (firedEventIds.has(eventId)) continue; // scoring events are static/generic, not per-message \u2014 count once
        firedEventIds.add(eventId);
        events.push({
          eventId,
          evidence: `${row.subject}: ${row.action}.`,
          kind: eventId === "inbox_used_legitimate_login" ? "positive" : "risk",
        });
      }
    }
    audio.click();
    onSubmit({ overallKey, correctCount, totalScored: total, events, actionRows });
  };

  const openQrPhone = () => {
    audio.click();
    setQrStage("phone");
  };
  const resolveQr = (key: "risky" | "safe", opt: UsbChoiceOption) => {
    if (key === "risky") audio.chimeBad();
    else audio.chimeGood();
    setQrOutcome({ key, choiceId: opt.id, eventId: opt.eventId });
    setQrStage(key === "risky" ? "recovery" : "resolved");
  };
  const resolveQrRecovery = (opt: UsbChoiceOption) => {
    if (opt.key === "risky") audio.chimeBad();
    else audio.chimeGood();
    setQrRecovery({ key: opt.key, choiceId: opt.id, eventId: opt.eventId });
    setQrStage("resolved");
  };

  const qrMsg = useMemo(() => config.emails.find((m) => !!m.qr) ?? null, [config.emails]);

  // Computed + hook call BEFORE the `showResults` early return below, so this
  // component always calls the same number/order of hooks regardless of
  // which branch renders — `useSpotlightCoachSide` is itself a hook (useState
  // + useEffect), and previously lived after the early return, which meant
  // the render that flips `showResults` to true suddenly called fewer hooks
  // than the render before it. React treats that as a fatal invariant
  // violation (minified error #300, "Rendered fewer hooks than expected")
  // and unmounts the whole tree, which is what made the screen go black the
  // instant "Submit my triage" was clicked.
  const tutorialTargets =
    tutorialStep === 0
      ? [senderToggleRef]
      : tutorialStep === 1
      ? [ctaRef]
      : tutorialStep === 2
      ? [verdictGroupRef]
      : [];
  const coachSide = useSpotlightCoachSide(rootRef, tutorialTargets, tutorialStep !== null);

  if (showResults) {
    const counts = { good: 0, mediocre: 0, risk: 0 };
    for (const r of actionRows) counts[r.tier]++;
    return (
      <div className="winapp mail inbox">
        <WindowTitlebar title={config.appTitle ?? tx("Outlook — Inbox")} />
        <div className="inbox-results">
          <h2>{tx("Your inbox results")}</h2>
          <p className="inbox-results-sub">
            {txr("{good} good · {mediocre} could be safer · {risk} high risk", { good: counts.good, mediocre: counts.mediocre, risk: counts.risk })}
          </p>
          <div className="psort-results-table inbox-rt" role="table" aria-label={tx("Your inbox rating")}>
            <div className="psort-rt-row psort-rt-head" role="row">
              <span className="psort-rt-cell psort-rt-item" role="columnheader">{tx("Message")}</span>
              <span className="psort-rt-cell psort-rt-status" role="columnheader">{tx("What you did")}</span>
              <span className="psort-rt-cell psort-rt-explain" role="columnheader">{tx("Why it matters")}</span>
            </div>
            {actionRows.map((row) => (
              <div key={row.messageId} className={`psort-rt-row inbox-rt-row--${row.tier}`} role="row">
                <span className="psort-rt-cell psort-rt-item" role="cell">
                  <span className="psort-rt-label">{row.subject}</span>
                  <span className="inbox-rt-from">{row.from}</span>
                </span>
                <span className="psort-rt-cell psort-rt-status" role="cell">
                  <span className={`psort-rt-badge inbox-rt-badge--${row.tier}`}>
                    {TIER_META[row.tier].ico} {TIER_META[row.tier].label}
                  </span>
                  <span className="inbox-rt-yours">{row.action}</span>
                </span>
                <span className="psort-rt-cell psort-rt-explain" role="cell">{row.note}</span>
              </div>
            ))}
          </div>
          <button type="button" className="btn psort-submit" onClick={finish}>
            {tx("Continue")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="winapp mail inbox" ref={rootRef}>
      <Spotlight containerRef={rootRef} targets={tutorialTargets} active={tutorialStep !== null} />
      {tutorialStep !== null && (
        <div className={`inbox-tutorial-coach inbox-tutorial-coach--${coachSide}`} role="status">
          <span className="inbox-tutorial-coach-step">{INBOX_TUTORIAL_COPY[tutorialStep].n}</span>
          <span className="inbox-tutorial-coach-body">{INBOX_TUTORIAL_COPY[tutorialStep].body}</span>
          <div className="inbox-tutorial-coach-actions">
            <button type="button" className="inbox-tutorial-coach-next" onClick={advanceTutorial}>
              {tx("Got it, next")}
            </button>
            <button
              type="button"
              className="inbox-tutorial-coach-skip"
              onClick={() => {
                audio.click();
                finishTutorial();
              }}
            >
              {tx("Skip tutorial")}
            </button>
          </div>
        </div>
      )}
      <WindowTitlebar title={config.appTitle ?? tx("Outlook — Inbox")} />
      <div className="mail-toolbar">
        <button className="mail-new" type="button">{"\u2709"} {tx("New mail")}</button>
        <span className="mail-tool">{"\u{1F5D1}"} {tx("Delete")}</span>
        <span className="mail-tool">{"\u{1F4E6}"} {tx("Archive")}</span>
        <span className="mail-tool">{"\u26A0"} {tx("Report")}</span>
        <span className="inbox-progress">
          {txr("{verdictCount}/{length} triaged", { verdictCount, length: config.emails.length })}
          {requiredVerdicts < config.emails.length && (
            <span className="inbox-progress-min"> &middot; {txr("{requiredVerdicts} needed to continue", { requiredVerdicts })}</span>
          )}
        </span>
        <button type="button" className="inbox-help-btn" onClick={() => setShowHelp(true)} title={tx("How this works & how it's scored")}>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
            <path d="M9.4 9.2a2.7 2.7 0 0 1 5 1.2c0 1.7-2.4 2-2.4 3.4M12 17h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {tx("How it works")}
        </button>
      </div>

      <div className="mail-body">
        <div className="mail-list" role="list">
          {config.emails.map((m) => {
            const unread = m.unread && !read.has(m.id);
            const v = verdicts[m.id];
            return (
              <button
                key={m.id}
                role="listitem"
                type="button"
                className={`mail-item ${selectedId === m.id ? "active" : ""} ${unread ? "unread" : ""}`}
                onClick={() => open(m)}
              >
                <span className="mail-avatar">{initialsOf(m)}</span>
                <span className="mail-item-text">
                  <span className="mail-from">{m.from}</span>
                  <span className="mail-subject">{m.subject}</span>
                  <span className="mail-preview">{m.preview}</span>
                </span>
                {v ? (
                  <span className={`inbox-tag inbox-tag--${VERDICT_TAG[v].cls}`}>{VERDICT_TAG[v].label}</span>
                ) : unread ? (
                  <span className="mail-dot" aria-hidden="true" />
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="mail-read">
          {selected ? (
            <article className="mail-msg">
              <div className="mail-msg-scroll">
              <h2 className="mail-msg-subject">{selected.subject}</h2>
              <div className="mail-msg-meta">
                <span className="mail-avatar lg">{initialsOf(selected)}</span>
                <span className="mail-msg-meta-text">
                  <span className="mail-msg-from">
                    {selected.from}
                    {selected.fromAddress && <span className="mail-msg-addr"> &lt;{selected.fromAddress}&gt;</span>}
                  </span>
                  <span className="mail-msg-to">
                    {txr("To: {v} {v2}", { v: selected.to ?? tx("you"), v2: selected.receivedAt ? `\u00B7 ${selected.receivedAt}` : "" })}
                  </span>
                  <button
                    type="button"
                    ref={senderToggleRef}
                    className={`inbox-sender-toggle ${tutorialStep === 0 ? "cf-pulse-target" : ""}`}
                    onClick={() => {
                      toggleSenderDetails(selected.id);
                      if (tutorialStep === 0) advanceTutorial();
                    }}
                  >
                    {txr("{v} Sender details", { v: expandedSender.has(selected.id) ? "\u25BE" : "\u25B8" })}
                  </button>
                  {expandedSender.has(selected.id) && (
                    <div className="inbox-sender-panel">
                      <div>
                        <b>{tx("From address:")}</b> {selected.fromAddress ?? "\u2014"}
                      </div>
                      <div className={selected.replyTo ? "inbox-replyto-mismatch" : undefined}>
                        <b>{tx("Reply-To:")}</b> {selected.replyTo ?? tx("(same as sender)")}
                      </div>
                      {selected.replyTo && (
                        <div className="inbox-sender-hint">
                          {"\u26A0"} {tx("Replies to this message would go somewhere different than the sender address.")}
                        </div>
                      )}
                    </div>
                  )}
                </span>
              </div>
              <div className="mail-msg-body">
                {selected.body.split(/\n{2,}/).map((para, i) => (
                  <p key={i}>
                    {para.split(/\n/).map((ln, j) => (
                      <span key={j}>
                        {ln}
                        {j < para.split(/\n/).length - 1 ? <br /> : null}
                      </span>
                    ))}
                  </p>
                ))}
                {selected.cta && (
                  <div className="mail-cta-wrap">
                    <button
                      type="button"
                      ref={ctaRef}
                      className={`mail-cta ${tutorialStep === 1 ? "cf-pulse-target" : ""}`}
                      onMouseEnter={() => setHoverHref(selected.cta!.url)}
                      onMouseLeave={() => setHoverHref(null)}
                      onFocus={() => setHoverHref(selected.cta!.url)}
                      onBlur={() => setHoverHref(null)}
                      onClick={() => {
                        if (tutorialStep === 1) advanceTutorial();
                        clickCta(selected);
                      }}
                    >
                      {selected.cta.label}
                    </button>
                    <a
                      className="mail-cta-url"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        clickCta(selected);
                      }}
                      onMouseEnter={() => setHoverHref(selected.cta!.url)}
                      onMouseLeave={() => setHoverHref(null)}
                    >
                      {selected.cta.displayUrl ?? selected.cta.url}
                    </a>
                    {linkOutcomes[selected.id] && (
                      <span className="inbox-cta-done">
                        {linkOutcomes[selected.id] === "submitted" ? tx("✓ Page action completed") : tx("✓ Link opened")}
                      </span>
                    )}
                  </div>
                )}
                {selected.attachments && selected.attachments.length > 0 && (
                  <div className="mail-attachments">
                    <div className="mail-attach-head">
                      {"\u{1F4CE}"} {txr("{length} attachment{v}", { length: selected.attachments.length, v: selected.attachments.length > 1 ? tx("s") : "" })}
                    </div>
                    {selected.attachments.map((att) => (
                      <button
                        key={att.id}
                        type="button"
                        className={`mail-attach mail-attach--btn ${att.danger ? "danger" : ""}`}
                        onClick={() => clickAttachment(selected, att.id, att.sim)}
                      >
                        <span className="mail-attach-ico">{ATT_ICON[att.type ?? "doc"] ?? "\u{1F4C4}"}</span>
                        <span className="mail-attach-meta">
                          <span className="mail-attach-name">{att.name}</span>
                          {att.size && <span className="mail-attach-size">{att.size}</span>}
                        </span>
                        {attOutcomes[att.id] && (
                          <span className="inbox-cta-done">
                            {attOutcomes[att.id] === "executed" ? tx("⚠ Activated") : tx("✓ Opened")}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {selected.qr && (
                  <div className="inbox-qr-block">
                    <div className="inbox-qr-code" aria-hidden>
                      <div className="inbox-qr-code-inner" />
                    </div>
                    <div className="inbox-qr-caption">
                      {selected.qr.qrCaption ?? tx("Scan to confirm your enrolment")}
                    </div>
                    {qrStage === "resolved" ? (
                      <div className="inbox-qr-done">
                        {"\u2713"} {tx("You already reviewed this code.")}
                      </div>
                    ) : (
                      <button type="button" className="btn inbox-qr-scan" onClick={openQrPhone}>
                        {selected.qr.scanButtonLabel ?? tx("Simulate scanning with your phone")}
                      </button>
                    )}
                  </div>
                )}
              </div>
              </div>

              <div className="inbox-verdict">
                <div className="inbox-verdict-q">{tx("What do you do with this message?")}</div>
                <div className="inbox-verdict-btns" ref={verdictGroupRef}>
                  {(["report", "legitimate", "verify"] as Verdict[]).map((v) => (
                    <button
                      key={v}
                      type="button"
                      className={`inbox-verdict-btn inbox-verdict-btn--${v} ${verdicts[selected.id] === v ? "active" : ""} ${tutorialStep === 2 ? "cf-pulse-target" : ""}`}
                      onClick={() => {
                        if (tutorialStep === 2) advanceTutorial();
                        setVerdict(selected, v);
                      }}
                    >
                      {v === "report" && <>{"\u{1F6A9}"} {tx("Report as phishing")}</>}
                      {v === "legitimate" && <>{"\u2705"} {tx("Looks legitimate")}</>}
                      {v === "verify" && <>{"\u{1F50D}"} {tx("Verify before acting")}</>}
                    </button>
                  ))}
                </div>
                {verdicts[selected.id] && selected.verdictFeedback?.[verdicts[selected.id]] && (
                  <div
                    className={`inbox-verdict-fb ${
                      selected.correctVerdict && selected.correctVerdict === verdicts[selected.id] ? "good" : "info"
                    }`}
                  >
                    {selected.verdictFeedback[verdicts[selected.id]]}
                  </div>
                )}
              </div>
            </article>
          ) : (
            <div className="mail-empty">
              <div className="mail-empty-art">{"\u2709"}</div>
              <div className="mail-empty-title">{tx("Select a message to review it")}</div>
              <div className="mail-empty-sub">
                {requiredVerdicts < config.emails.length
                  ? tx("Open at least {requiredVerdicts} messages and decide what to do with each one — the more you review, the better.", { requiredVerdicts })
                  : tx("Open every message in your inbox, then decide what to do with each one.")}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={`mail-statusbar ${hoverHref ? "show" : ""}`}>{hoverHref ?? ""}</div>

      <div className="inbox-submit-bar">
        {!canSubmit && (
          <span className="inbox-submit-hint">
            {qrBusy
              ? tx("Finish reviewing the QR code first.")
              : (config.incompleteHint ?? tx("{remaining} message{v} still need a decision.", { remaining, v: remaining === 1 ? "" : "s" }))}
          </span>
        )}
        <button type="button" className={`btn inbox-submit ${canSubmit ? "ready" : ""}`} disabled={!canSubmit} onClick={submit}>
          {config.submitLabel ?? tx("Submit my triage")}
        </button>
      </div>

      {qrMsg?.qr && qrStage === "phone" && (
        <div className="inbox-qr-overlay">
          <div className="inbox-qr-phone">
            <div className="inbox-qr-phone-notch" />
            <div className="inbox-qr-addr">
              <span className="inbox-qr-lock" aria-hidden>{"\u{1F513}"}</span> {qrMsg.qr.spoofUrl}
            </div>
            <div className="inbox-qr-page">
              <div className="inbox-qr-page-title">{qrMsg.qr.loginHeadline ?? tx("Confirm your identity")}</div>
              <label className="inbox-qr-label">{tx("Work email")}</label>
              <input
                className="inbox-qr-input"
                type="text"
                value={qrEmail}
                onChange={(e) => setQrEmail(e.target.value)}
                placeholder="you@northbayfinancial.com"
              />
              <label className="inbox-qr-label">{tx("Password")}</label>
              <input
                className="inbox-qr-input cf-mask"
                type="text"
                value={qrPassword}
                onChange={(e) => setQrPassword(e.target.value)}
                placeholder={tx("Enter your password")}
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
              />
              <button
                type="button"
                className="btn inbox-qr-signin"
                onClick={() => resolveQr("risky", qrMsg.qr!.options.enter)}
              >
                {tx("Sign In")}
              </button>
              <button
                type="button"
                className="inbox-qr-back"
                onClick={() => resolveQr("safe", qrMsg.qr!.options.backOut)}
              >
                {"\u2190"} {tx("This doesn't feel right — go back")}
              </button>
            </div>
          </div>
        </div>
      )}

      {qrStage === "recovery" && (
        <div className="inbox-qr-overlay">
          <div className="inbox-qr-recovery">
            <div className="inbox-qr-recovery-ico" aria-hidden>{"\u26A0"}</div>
            <div className="inbox-qr-recovery-title">{tx("That page just took your work email and password.")}</div>
            <ChoiceList prompt={tx("What do you do right now?")}>
              {QR_RECOVERY_OPTIONS.map((opt) => (
                <ChoiceCard
                  key={opt.id}
                  category={opt.key === "risky" ? "comply" : "verify"}
                  reply={opt.label}
                  showRiskLabel={false}
                  onClick={() => resolveQrRecovery(opt)}
                />
              ))}
            </ChoiceList>
          </div>
        </div>
      )}

      {linkView && selected?.id === linkView && selected.cta?.sim && (
        <LinkSimOverlay
          sim={selected.cta.sim}
          onOutcome={(outcome) => setLinkOutcomes((p) => ({ ...p, [selected.id]: outcome }))}
          onClose={() => setLinkView(null)}
        />
      )}

      {attView && (
        <AttachmentSimOverlay
          name={config.emails.find((m) => m.id === attView.messageId)?.attachments?.find((a) => a.id === attView.attId)?.name ?? "file"}
          sim={
            config.emails.find((m) => m.id === attView.messageId)?.attachments?.find((a) => a.id === attView.attId)?.sim ??
            ({ kind: "office_macro" } as InboxAttachmentSim)
          }
          onOutcome={(outcome) => setAttOutcomes((p) => ({ ...p, [attView.attId]: outcome }))}
          onClose={() => setAttView(null)}
        />
      )}

      {showHelp && (
        <div className="inbox-qr-overlay inbox-help-overlay" role="dialog" aria-label={tx("How the inbox challenge works")}>
          <div className="inbox-help-card">
            <div className="inbox-help-head">
              <h3>{tx("How the inbox challenge works")}</h3>
              <button type="button" className="inbox-help-close" onClick={() => setShowHelp(false)} aria-label={tx("Close")}>
                {"\u2715"}
              </button>
            </div>
            <p className="inbox-help-lede">
              {txr("Open each message and decide: <0>Report as phishing</0>,<1> Looks legitimate</1>, or <2>Verify before acting</2>. You must review at least <3>{requiredVerdicts} of {length}</3> messages before you can submit.", { requiredVerdicts, length: config.emails.length }, [<strong />, <strong />, <strong />, <strong />])}
            </p>
            <div className="inbox-help-block">
              <div className="inbox-help-block-title">{tx("How to investigate")}</div>
              <ul>
                <li>{txr("Click <0>Sender details</0> to reveal the real From address and Reply-To — the display name lies, the address often gives it away.", {}, [<strong />])}</li>
                <li>{txr("<0>Hover</0> over any link to preview where it really goes (shown at the bottom of the window) — you don't have to click it.", {}, [<strong />])}</li>
                <li>{txr("You <0>can</0> click a link or open a file to see what it does — but only to learn. It affects your score (below).", {}, [<em />])}</li>
              </ul>
            </div>
            <div className="inbox-help-block inbox-help-block--score">
              <div className="inbox-help-block-title">{tx("How it's scored")}</div>
              <ul>
                <li><span className="inbox-help-dot good" /> {txr("Best: judge a message correctly <0>without clicking the link or opening the attachment</0>", {}, [<strong />])}.</li>
                <li><span className="inbox-help-dot warn" /> {tx("Weaker: opening a suspicious link or attachment — even just to look — is a risk on its own.")}</li>
                <li><span className="inbox-help-dot bad" /> {tx("Worst: signing in on a lookalike page, downloading the file, or enabling macros.")}</li>
              </ul>
              <p className="inbox-help-tip">{tx("A safe reviewer decides from the clues (sender, tone, link preview) — not by clicking through.")}</p>
            </div>
            <button type="button" className="btn inbox-help-done" onClick={() => setShowHelp(false)}>
              {tx("Got it — start reviewing")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
